import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Message as MessageIcon,
  Add as AddIcon,
  TrendingUp,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ClientDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [client, setClient] = useState(null);

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setClient({
        id: 1,
        initial: 'NT',
        name: 'Northwind Trading Ltd',
        industry: 'Wholesale',
        vatNumber: 'IE3284561AH',
        registration: '628194',
        status: 'Active',
        leadAccountant: 'Aoife Byrne',
        revenueYTD: 842000,
        expensesYTD: 612000,
        netProfit: 230000,
        outstandingBalance: 18420,
        profitMargin: 27,
        revenueData: [
          { month: 'Jan', Revenue: 118000, Expenses: 82000 },
          { month: 'Feb', Revenue: 142000, Expenses: 95000 },
          { month: 'Mar', Revenue: 156000, Expenses: 108000 },
          { month: 'Apr', Revenue: 138000, Expenses: 98000 },
          { month: 'May', Revenue: 162000, Expenses: 115000 },
          { month: 'Jun', Revenue: 126000, Expenses: 114000 },
        ],
        upcomingDeadlines: [
          { id: 1, title: 'VAT return Q2 2026', dueDate: '2026-07-19', icon: 'vat' },
          { id: 2, title: 'Payroll submission', dueDate: '2026-07-03', icon: 'payroll' },
          { id: 3, title: 'Annual accounts', dueDate: '2026-09-30', icon: 'accounts' },
        ],
        invoices: [
          { id: 'OUT-1042', type: 'Sales', date: '2026-06-29', total: 10332, status: 'Unpaid' },
          { id: 'OUT-1041', type: 'Sales', date: '2026-06-27', total: 12000, status: 'Paid' },
          { id: 'OUT-1040', type: 'Sales', date: '2026-06-24', total: 3936, status: 'Overdue' },
          { id: 'OUT-1039', type: 'Sales', date: '2026-06-20', total: 22632, status: 'Unpaid' },
          { id: 'INV-2284', type: 'Purchase', date: '2026-06-28', total: 2952, status: 'Unpaid' },
          { id: 'INV-2285', type: 'Purchase', date: '2026-06-26', total: 472, status: 'Overdue' },
          { id: 'INV-2286', type: 'Purchase', date: '2026-06-25', total: 6298, status: 'Unpaid' },
          { id: 'INV-2287', type: 'Purchase', date: '2026-06-24', total: 1156, status: 'Paid' },
        ],
        bankAccount: {
          name: 'AIB Business Current',
          iban: 'IE29 AIBK 9311 5212 3456 78',
          balance: 42920,
        },
        documents: [
          { id: 1, name: 'Dell_Invoice_2284.pdf', folder: 'Purchase Invoices', type: 'PDF', uploaded: '2h ago', status: 'Processed' },
          { id: 2, name: 'AIB_Statement_June.pdf', folder: 'Bank Statements', type: 'PDF', uploaded: '5h ago', status: 'Processed' },
          { id: 3, name: 'VAT_Return_Q1.xml', folder: 'Tax Documents', type: 'XML', uploaded: '1d ago', status: 'Processed' },
          { id: 4, name: 'Lease_Agreement_2026.pdf', folder: 'Contracts', type: 'PDF', uploaded: '2d ago', status: 'Review' },
          { id: 5, name: 'Payroll_June.xlsx', folder: 'Payroll', type: 'XLSX', uploaded: '3d ago', status: 'Processed' },
          { id: 6, name: 'Receipt_Catering.jpg', folder: 'Purchase Invoices', type: 'JPG', uploaded: '6h ago', status: 'Processing' },
        ],
        tasks: [
          { id: 1, title: 'Prepare Q2 VAT return', assignee: 'Aoife Byrne', dueDate: '2026-07-19', priority: 'High', completed: false },
          { id: 2, title: 'Reconcile June bank statement', assignee: 'Priya Nair', dueDate: '2026-07-05', priority: 'High', completed: false },
          { id: 3, title: 'Chase missing receipts', assignee: 'Aoife Byrne', dueDate: '2026-07-08', priority: 'Medium', completed: false },
          { id: 4, title: 'Year-end accounts review', assignee: 'Priya Nair', dueDate: '2026-08-12', priority: 'Medium', completed: false },
          { id: 5, title: 'Payroll submission', assignee: 'Marcus Klein', dueDate: '2026-07-03', priority: 'High', completed: false },
        ],
        contacts: [
          { id: 1, name: 'Jane Doyle', role: 'Finance Director', email: 'jane@example.com', initial: 'JD' },
          { id: 2, name: 'Michael Ryan', role: 'Owner', email: 'michael@example.com', initial: 'MR' },
        ],
      });
      setLoading(false);
    }, 500);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
      case 'Processed':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Unpaid':
      case 'Review':
        return { bg: '#E3F2FD', color: '#2196F3' };
      case 'Overdue':
        return { bg: '#FFEBEE', color: '#F44336' };
      case 'Processing':
        return { bg: '#FFF3E0', color: '#FF9800' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return { bg: '#FFEBEE', color: '#F44336' };
      case 'Medium':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Low':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  if (loading) {
    return (
      <AccountantLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AccountantLayout>
    );
  }

  return (
    <AccountantLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/accountant-portal/clients')}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            {t('all_clients')}
          </Button>
        </Box>

        {/* Client Header */}
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#E3F2FD',
                color: '#1976d2',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              {client.initial}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {client.name}
                </Typography>
                <Chip
                  label={client.status}
                  size="small"
                  sx={{
                    backgroundColor: '#E8F5E9',
                    color: '#4CAF50',
                    fontWeight: 500,
                  }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                {client.industry} · VAT {client.vatNumber} · Reg {client.registration}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<MessageIcon />}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              {t('message')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                },
              }}
            >
              {t('new_invoice')}
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {t('revenue_ytd')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  €{client.revenueYTD.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {t('expenses_ytd')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  €{client.expensesYTD.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {t('net_profit')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                  €{client.netProfit.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {t('outstanding_balance')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  €{client.outstandingBalance.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {t('profit_margin')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {client.profitMargin}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ borderRadius: '12px', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            <Tab label={t('overview')} />
            <Tab label={t('invoices')} />
            <Tab label={t('banking')} />
            <Tab label={t('documents')} />
            <Tab label={t('tasks')} />
            <Tab label={t('details')} />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('revenue_vs_expenses')}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={client.revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Revenue" fill="#4361EE" />
                    <Bar dataKey="Expenses" fill="#3A7562" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {t('upcoming_deadlines')}
                </Typography>
                {client.upcomingDeadlines.map((deadline) => (
                  <Box
                    key={deadline.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 2,
                      backgroundColor: '#FFF9E6',
                      borderRadius: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: '#FFF3CD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {deadline.icon === 'vat' && <ReceiptIcon sx={{ color: '#856404' }} />}
                      {deadline.icon === 'payroll' && <DescriptionIcon sx={{ color: '#856404' }} />}
                      {deadline.icon === 'accounts' && <EventIcon sx={{ color: '#856404' }} />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {deadline.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Due {deadline.dueDate}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('invoices')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>NUMBER</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>TYPE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>DATE</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {client.invoices.map((invoice) => {
                    const statusColor = getStatusColor(invoice.status);
                    return (
                      <TableRow key={invoice.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{invoice.id}</TableCell>
                        <TableCell>{invoice.type}</TableCell>
                        <TableCell>{invoice.date}</TableCell>
                        <TableCell align="right">€{invoice.total.toLocaleString()}.00</TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            size="small"
                            icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColor.color, ml: 1 }} />}
                            sx={{
                              backgroundColor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('bank_account')}
            </Typography>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #4361EE 0%, #3A0CA3 100%)',
                color: 'white',
                p: 3,
                borderRadius: '12px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <BankIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {client.bankAccount.name}
                  </Typography>
                  <Typography variant="body2">{client.bankAccount.iban}</Typography>
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                €{client.bankAccount.balance.toLocaleString()}
              </Typography>
            </Card>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {t('bank_transactions_auto_import')}
            </Typography>
          </Paper>
        )}

        {activeTab === 3 && (
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('documents')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>NAME</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>FOLDER</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>TYPE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>UPLOADED</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {client.documents.map((doc) => {
                    const statusColor = getStatusColor(doc.status);
                    return (
                      <TableRow key={doc.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{doc.name}</TableCell>
                        <TableCell>{doc.folder}</TableCell>
                        <TableCell>{doc.type}</TableCell>
                        <TableCell>{doc.uploaded}</TableCell>
                        <TableCell>
                          <Chip
                            label={doc.status}
                            size="small"
                            icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColor.color, ml: 1 }} />}
                            sx={{
                              backgroundColor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {activeTab === 4 && (
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('tasks')}
            </Typography>
            {client.tasks.map((task) => {
              const priorityColor = getPriorityColor(task.priority);
              return (
                <Box
                  key={task.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <Checkbox checked={task.completed} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {task.assignee} · Due {task.dueDate}
                    </Typography>
                  </Box>
                  <Chip
                    label={task.priority}
                    size="small"
                    icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: priorityColor.color, ml: 1 }} />}
                    sx={{
                      backgroundColor: priorityColor.bg,
                      color: priorityColor.color,
                      fontWeight: 500,
                    }}
                  />
                </Box>
              );
            })}
          </Paper>
        )}

        {activeTab === 5 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  {t('company_details')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('legal_name')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.name}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('vat_number')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.vatNumber}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('registration')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.registration}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('industry')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.industry}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('lead_accountant')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {client.leadAccountant}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('status')}
                    </Typography>
                    <Chip
                      label={client.status}
                      size="small"
                      icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#4CAF50', ml: 1 }} />}
                      sx={{
                        backgroundColor: '#E8F5E9',
                        color: '#4CAF50',
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  {t('contacts')}
                </Typography>
                {client.contacts.map((contact) => (
                  <Box
                    key={contact.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                    }}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: '#E3F2FD',
                        color: '#1976d2',
                        fontWeight: 'bold',
                      }}
                    >
                      {contact.initial}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {contact.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {contact.role} · {contact.email}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </AccountantLayout>
  );
};

export default ClientDetailPage;